import React, { useState, forwardRef } from 'react';
import { RefereeInfo } from '../../types/forms';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Tooltip,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import ArrowBack from '@mui/icons-material/ArrowBack';

interface RefereeTabProps {
  formData: RefereeInfo;
  onChange: (data: RefereeInfo) => void;
  onBack: () => void;
  onSave: () => void;
  saveStatus: 'saved' | 'error' | null;
}

interface RefereeErrors {
  firstReferee: {
    name?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    relationship?: string;
    yearsKnown?: string;
    address?: string;
    occupation?: string;
    organisation?: string;
    jobTitle?: string;
    company?: string;
  };
  secondReferee: {
    name?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    relationship?: string;
    yearsKnown?: string;
    address?: string;
    occupation?: string;
    organisation?: string;
    jobTitle?: string;
    company?: string;
  };
}

type RefereeField = 'name' | 'email' | 'phone' | 'mobile' | 'relationship' | 'yearsKnown' | 'jobTitle' | 'company';

const RefereeTab = forwardRef<HTMLDivElement, RefereeTabProps>(({ 
  formData, 
  onChange, 
  onBack, 
  onSave, 
  saveStatus 
}, ref) => {
  const [errors, setErrors] = useState<RefereeErrors>({
    firstReferee: {},
    secondReferee: {}
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s+()-]{10,}$/;
    return phoneRegex.test(phone);
  };

  const validateField = (field: RefereeField, value: string, refereeType: 'firstReferee' | 'secondReferee'): string => {
    switch (field) {
      case 'name':
        if (!value?.trim()) {
          return 'Name is required';
        }
        return '';
      case 'email':
        if (!value?.trim()) {
          return 'Email is required';
        }
        if (!validateEmail(value)) {
          return 'Please enter a valid email address';
        }
        return '';
      case 'phone':
        if (value?.trim() && !validatePhone(value)) {
          return 'Please enter a valid phone number';
        }
        return '';
      case 'mobile':
        if (!value?.trim()) {
          return 'Mobile number is required';
        }
        if (!validatePhone(value)) {
          return 'Please enter a valid mobile number';
        }
        return '';
      case 'relationship':
        if (!value?.trim()) {
          return 'Relationship is required';
        }
        return '';
      case 'yearsKnown':
        if (!value?.trim()) {
          return 'Years known is required';
        }
        return '';



      case 'jobTitle':
        // Job title is optional, no validation needed
        return '';
      case 'company':
        // Company is optional, no validation needed
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (
    refereeType: 'firstReferee' | 'secondReferee',
    field: RefereeField,
    value: string
  ) => {
    try {
      const error = validateField(field, value, refereeType);
      
      setErrors(prev => ({
        ...prev,
        [refereeType]: {
          ...prev[refereeType],
          [field]: error
        }
      }));

      onChange({
        ...formData,
        [refereeType]: {
          ...formData[refereeType],
          [field]: value
        }
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrors(prev => ({
        ...prev,
        [refereeType]: {
          ...prev[refereeType],
          [field]: errorMessage
        }
      }));
    }
  };

  const renderRefereeForm = (
    refereeType: 'firstReferee' | 'secondReferee',
    title: string,
    referee: RefereeInfo['firstReferee'] | RefereeInfo['secondReferee']
  ) => {
    const helperText = refereeType === 'firstReferee'
      ? 'Current or recent employer, or professional contact'
      : 'Someone who has known you for at least 3 years (e.g. tutor, supervisor)';
    const refereeErrors = errors[refereeType];

    return (
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {title}
            <Tooltip title={helperText}>
              <IconButton size="small" sx={{ ml: 1 }}>
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>{helperText}</FormHelperText>
          <Grid container spacing={2}>
            {/* Row 1: Name and Relationship */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={referee.name || ''}
                onChange={(e) => handleFieldChange(refereeType, 'name', e.target.value)}
                error={!!refereeErrors.name}
                helperText={refereeErrors.name || 'Full name of referee'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Relationship"
                value={referee.relationship || ''}
                onChange={(e) => handleFieldChange(refereeType, 'relationship', e.target.value)}
                error={!!refereeErrors.relationship}
                helperText={refereeErrors.relationship || 'How do you know this referee?'}
                required
              />
            </Grid>
            
            {/* Row 2: Job Title and Company/Organisation */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={referee.jobTitle || ''}
                onChange={(e) => handleFieldChange(refereeType, 'jobTitle', e.target.value)}
                error={!!refereeErrors.jobTitle}
                helperText={refereeErrors.jobTitle || 'Referee\'s job title'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={refereeType === 'secondReferee' ? 'Organisation' : 'Company'}
                value={referee.company || ''}
                onChange={(e) => handleFieldChange(refereeType, 'company', e.target.value)}
                error={!!refereeErrors.company}
                helperText={refereeErrors.company || (refereeType === 'secondReferee' ? 'Organisation name' : 'Company name (if applicable)')}
                required
              />
            </Grid>
            
            {/* Row 3: Years Known and Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Years Known"
                value={referee.yearsKnown || ''}
                onChange={(e) => handleFieldChange(refereeType, 'yearsKnown', e.target.value)}
                error={!!refereeErrors.yearsKnown}
                helperText={refereeErrors.yearsKnown || 'How many years have you known this referee?'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={referee.email}
                onChange={(e) => handleFieldChange(refereeType, 'email', e.target.value)}
                error={!!refereeErrors.email}
                helperText={refereeErrors.email || 'Professional email address'}
                required
              />
            </Grid>
            
            {/* Row 4: Mobile and Phone */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={referee.mobile}
                onChange={(e) => handleFieldChange(refereeType, 'mobile', e.target.value)}
                error={!!refereeErrors.mobile}
                helperText={refereeErrors.mobile || 'Mobile number'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={referee.phone || ''}
                onChange={(e) => handleFieldChange(refereeType, 'phone', e.target.value)}
                error={!!refereeErrors.phone}
                helperText={refereeErrors.phone || 'Main phone number'}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  };

  return (
    <div ref={ref}>
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please provide contact details for two referees:
          <ul>
            <li>Professional referee should be a current or recent employer, or someone you know in a professional basis</li>
            <li>Personal referee should be someone you have known for at least three years (e.g. tutor, supervisor)</li>
            <li><strong>Note:</strong> Referees cannot be family members or relatives</li>
            <li>By providing these details, you agree for us to contact these referees for verification purposes once you have been successful at interview </li>
          </ul>
        </Alert>
        <Grid container spacing={3}>
          {renderRefereeForm(
            'firstReferee',
            'Professional Referee',
            formData.firstReferee
          )}
          
          {renderRefereeForm(
            'secondReferee',
            'Personal Referee',
            formData.secondReferee
          )}
          
          <Grid item xs={12}>
            <Grid container spacing={2} justifyContent="space-between" sx={{ mt: 4 }}>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={onBack}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSave}
                >
                  Save Progress
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {saveStatus && (
            <Grid item xs={12}>
              <Alert severity={saveStatus === 'saved' ? 'success' : 'error'}>
                {saveStatus === 'saved' ? 'Changes saved successfully' : 'Error saving changes'}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </div>
  );
});

RefereeTab.displayName = 'RefereeTab';

export default RefereeTab;